import { Dropdown, type Option, SearchEngineType } from "@hoylu/client-common";
import DefaultDropdownStyle from "@hoylu/client-common/dist/assets/css.modules/default.dropdown.module.css";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import type { Project, WorkspaceProjects } from "../../state/workspaces/types";
import { selectProject } from "../../state/workspaces/workspaces.actions";
import { Localized } from "../../strings";
import { addPrefixWhenProjectIsInactive } from "../../utils/add.prefix.when.project.is.inactive";
import { useSingleSelectDropdownTranslation } from "../../utils/hooks/useSingleSelectDropdownTranslation";
import { getDashboardProjectOption } from "./get.dashboard.project.option";
import { getEmptyProjectOption } from "./get.empty.project.option";
import Styles from "./ProjectNavigationDropdown.module.css";
import { ProjectOptionRenderer } from "./ProjectOptionRenderer";
import { ProjectValueRenderer } from "./ProjectValueRenderer";
import type { ProjectOption } from "./types";

type ProjectNavigationDropdownProps = {
  allProjects: WorkspaceProjects;
  selectedProject: Project | null;
  showProjects: boolean;
  allowCreatingProject: boolean;
};

export const ProjectNavigationDropdown: React.FC<
  ProjectNavigationDropdownProps
> = ({ allProjects, selectedProject, showProjects, allowCreatingProject }) => {
  const strings = Localized.object("FILTER_BY_NAME");
  const dropdownTranslation = useSingleSelectDropdownTranslation();
  const dispatch = useDispatch();

  const DashboardProject = getDashboardProjectOption(() =>
    dispatch(selectProject(null)),
  );
  const EmptyProjectOption = getEmptyProjectOption();

  const projectsDropdownOptions: Option<ProjectOption>[] = [
    DashboardProject,
    ...allProjects.map((p) => ({
      label: addPrefixWhenProjectIsInactive(p),
      value: {
        ...p,
        onSelected: () => dispatch(selectProject(p)),
      },
    })),
  ];

  if (allowCreatingProject) {
    projectsDropdownOptions.push(EmptyProjectOption);
  }

  const value = useMemo(() => {
    return (
      projectsDropdownOptions.find(
        ({ value }) => value.id === selectedProject?.id,
      ) ?? DashboardProject
    );
  }, [selectedProject, projectsDropdownOptions]);

  if (!showProjects || !allProjects.length) {
    return null;
  }

  return (
    <div
      aria-label={strings.FILTER_BY_NAME}
      className={Styles.dropdownContainer}
      tabIndex={0}
    >
      <Dropdown
        allowReset={false}
        classes={[
          DefaultDropdownStyle.dropdown,
          Styles.projectNavigationDropdown,
        ]}
        dropdownClasses={[
          DefaultDropdownStyle.dropDownPopUp,
          Styles.dropDownPopUp,
        ]}
        ignoreScrollContext={true}
        localizationDict={dropdownTranslation}
        onChange={() => {}}
        optionRenderer={ProjectOptionRenderer}
        options={projectsDropdownOptions}
        searchEngineType={SearchEngineType.FUZZY}
        title={value.label}
        valueRenderer={ProjectValueRenderer}
        values={[value]}
      />
    </div>
  );
};

export default ProjectNavigationDropdown;
